/* eslint-disable no-redeclare */
import {
  BANK_ACCOUNT_TABLE_NAME,
  BankAccount,
  CreateBankAccountInput as CreateInput,
  CreateBankAccountMutationVariables as CreateMutationVariables,
  CustomSubscription,
  CustomSubscriptionType,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  DeleteBankAccountMutationVariables as DeleteMutationVariables,
  Model,
  OnCreateBankAccountSubscription,
  removeNestedFieldsExcept,
  OnCreateCustomSubscriptionSubscription,
  OnDeleteBankAccountSubscription,
  OnDeleteCustomSubscriptionSubscription,
  OnUpdateBankAccountSubscription,
  OnUpdateCustomSubscriptionSubscription,
  cleanInputCreate,
  cleanInputUpdate,
  createBankAccount as createMutation,
  deleteBankAccount as deleteMutation,
  getCleanBankAccountNumber,
  IbanStatus,
  getBankAccount as getQuery,
  getReadId,
  getTableClientId,
  getTypedCustomSubscriptionValueForRefetch,
  isClientOwnedBankAccount,
  isNilOrEmpty,
  onCreateBankAccount,
  onCreateCustomSubscription,
  onDeleteBankAccount,
  onDeleteCustomSubscription,
  onUpdateBankAccount,
  onUpdateCustomSubscription,
  setDefaultBankAccount as setDefaultBankAccountMutation,
  type SetDefaultBankAccountMutationVariables,
  syncBankAccounts as syncQuery,
  todayDateTime,
  updateBankAccount as updateMutation,
  verifyBeneficiaryIban as verifyBeneficiaryIbanMutation,
  VerifyBeneficiaryIbanMutationVariables,
  mergeFullyFetchedRecords,
} from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  mutationWithFetchBefore,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import React, { useContext, useEffect, useMemo, useRef } from 'react';

import { ContextLoaderAction, ContextLoaderStore, useContextLoader } from './ContextLoader';
import { invalidateOpenInvoices } from './openInvoicesInvalidation';
import { hasPaymentRelevantBankAccountChange, invalidatePayments } from './paymentsInvalidation';
import { useUser } from './UserContext';
import { sortBankAccountsByCreation } from './utils/bankAccountOrderUtils';
import { usePermissions } from './utils/PermissionsContext';

const ENTITY_MODEL_NAME: Model = 'BankAccount';

export type ActionBankAccount =
  | {
      type: 'SHOULD_FETCH_BANK_ACCOUNT' | 'IS_FETCHING_BANK_ACCOUNT';
    }
  | {
      type: 'ADD_BANK_ACCOUNT';
      payload: { bankAccount: BankAccount };
    }
  | {
      type: 'FETCHED_BANK_ACCOUNT';
      payload: { bankAccounts: BankAccount[]; idsAtFetchStart?: Set<string> };
    }
  | {
      type: 'UPDATE_BANK_ACCOUNT';
      payload: { bankAccount: BankAccount };
    }
  | {
      type: 'DELETE_BANK_ACCOUNT';
      payload: { id: string };
    };

export interface BankAccountContext extends BankAccountState {
  createBankAccount: (input: Omit<BankAccount, 'id' | 'clientId' | 'readId'>) => Promise<BankAccount | undefined>;
  getBankAccount: (id: string) => BankAccount | null | undefined;
  getBankAccountsForContact: (contactId: string) => BankAccount[];
  isBankAccountNumberTaken: (number: string, exceptBankAccountId?: string) => boolean;
  updateBankAccount: (original: BankAccount, updates: Partial<BankAccount>) => Promise<BankAccount>;
  markBankAccountNotVerified: (original: BankAccount) => Promise<BankAccount>;
  verifyBeneficiaryIban: (bankAccount: BankAccount) => Promise<BankAccount>;
  setDefaultBankAccount: (original: BankAccount) => Promise<BankAccount>;
  deleteBankAccount: (id: string) => Promise<BankAccount>;
  setFetchBankAccount: () => void;
}

interface BankAccountState {
  bankAccounts: BankAccount[];
  supplierBankAccounts: BankAccount[];
  loading: boolean;
  shouldFetchBankAccount: boolean;
  error: string | null;
}

export const bankAccountReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_BANK_ACCOUNT':
      if (state.BankAccount.loading || state.BankAccount.shouldFetch) {
        return state;
      }
      return {
        ...state,
        BankAccount: { ...state.BankAccount, shouldFetch: true },
      };
    case 'IS_FETCHING_BANK_ACCOUNT':
      if (state.BankAccount.loading) {
        return state;
      }
      return {
        ...state,
        BankAccount: { ...state.BankAccount, loading: isNil(state.BankAccount.lastFetch) },
      };
    case 'FETCHED_BANK_ACCOUNT':
      return {
        ...state,
        BankAccount: {
          ...state.BankAccount,
          data: mergeFullyFetchedRecords(
            state.BankAccount.data,
            action.payload.bankAccounts,
            action.payload.idsAtFetchStart
          ),
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_BANK_ACCOUNT':
      // Check if already present - If already added by this user or coming from another user
      if (state.BankAccount.data?.find((object) => object.id === action.payload.bankAccount.id)) {
        return state;
      }

      return {
        ...state,
        BankAccount: {
          ...state.BankAccount,
          data: [...state.BankAccount.data, action.payload.bankAccount],
        },
      };
    case 'UPDATE_BANK_ACCOUNT':
      // No data
      if (isNilOrEmpty(state.BankAccount.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.BankAccount.data?.find((object) => object.id === action.payload.bankAccount.id);
      if (!currentObject) {
        return {
          ...state,
          BankAccount: {
            ...state.BankAccount,
            data: [...state.BankAccount.data, action.payload.bankAccount],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.bankAccount)) {
        return state;
      }

      // Update
      return {
        ...state,
        BankAccount: {
          ...state.BankAccount,
          data: state.BankAccount.data.map((object) => {
            if (object.id === action.payload.bankAccount.id) {
              return action.payload.bankAccount;
            }
            return object;
          }),
        },
      };
    case 'DELETE_BANK_ACCOUNT':
      if (isNilOrEmpty(state.BankAccount.data)) {
        return state;
      }
      return {
        ...state,
        BankAccount: {
          ...state.BankAccount,
          data: state.BankAccount.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

const BANK_ACCOUNT_RELATIONS_TO_REMOVE = ['contact'];

const BACKEND_ONLY_BANK_ACCOUNT_FIELDS: (keyof BankAccount)[] = [
  'ibanVerifiedAt',
  'ibanVerifiedBy',
  'ibanVerifiedNumber',
  'ibanVerifiedSourceDocumentId',
  'isDefault',
  'paymentRunPriorityOrderIds',
  'paymentRunPriorityUpdatedAt',
  'paymentRunPriorityUpdatedBy',
];

const BANK_ACCOUNTS_QUERY = removeNestedFieldsExcept(
  syncQuery,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  BANK_ACCOUNT_RELATIONS_TO_REMOVE,
  ['customValues']
);

const fetchBankAccounts = async (clientId: string, additionalFilter?: object): Promise<BankAccount[]> => {
  return await list<BankAccount>(
    BANK_ACCOUNTS_QUERY,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME),
    additionalFilter
  );
};

export const BankAccountContext = React.createContext<BankAccountContext | null>(null);

export const BankAccountContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { clientId } = useUser();
  const queryClient = useQueryClient();
  const {
    BankAccount: { data: bankAccountsLoader, loading: bankAccountsLoading, shouldFetch },
    Contact: { data: contactsLoader, loading: contactsLoading },
    dispatch: contextDispatch,
  } = useContextLoader();
  const { financialTransactionsDelete } = usePermissions();
  const storedBankAccountsRef = useRef<BankAccount[]>(bankAccountsLoader);

  useEffect(() => {
    storedBankAccountsRef.current = bankAccountsLoader;
  }, [bankAccountsLoader]);

  const setFetchBankAccount = () => {
    contextDispatch({ type: 'SHOULD_FETCH_BANK_ACCOUNT' });
  };

  const loading = bankAccountsLoading || contactsLoading;

  const allBankAccounts = useMemo<BankAccount[]>(() => {
    if (loading) {
      return [];
    }

    const bankAccountsWithContact = bankAccountsLoader.map((bankAccount) => {
      const contact = contactsLoader.find((bankContact) => bankContact.id === bankAccount.contactId);
      return { ...bankAccount, contact };
    });
    return bankAccountsWithContact;
  }, [loading, bankAccountsLoader, contactsLoader]);

  const bankAccounts = useMemo<BankAccount[]>(
    () => allBankAccounts.filter(isClientOwnedBankAccount),
    [allBankAccounts]
  );
  const supplierBankAccounts = useMemo<BankAccount[]>(
    () => allBankAccounts.filter((bankAccount) => !isClientOwnedBankAccount(bankAccount)),
    [allBankAccounts]
  );

  useEffect(() => {
    const fetchAndSet = async () => {
      const idsAtFetchStart = new Set(bankAccountsLoader.map(({ id }) => id));
      contextDispatch({ type: 'IS_FETCHING_BANK_ACCOUNT' });
      try {
        const result = await fetchBankAccounts(clientId!);
        contextDispatch({ type: 'FETCHED_BANK_ACCOUNT', payload: { bankAccounts: result, idsAtFetchStart } });
      } catch (error) {
        console.error('[bankAccounts] fetching bank accounts failed', error);
        contextDispatch({ type: 'FETCH_FAILED', payload: { model: 'BankAccount' } });
      }
    };
    if (shouldFetch) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  const createBankAccount = async (
    input: Omit<BankAccount, 'id' | 'clientId' | 'readId'>
  ): Promise<BankAccount | undefined> => {
    const cleanNumber = getCleanBankAccountNumber(input.number);
    const bankAccountAlreadyExists = allBankAccounts.some(
      (currentBankAccount) => getCleanBankAccountNumber(currentBankAccount.number) === cleanNumber
    );
    if (bankAccountAlreadyExists) return undefined;

    const bankAccount = await mutation<BankAccount, CreateMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateInput),
        clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
        readId: getReadId(clientId!, ENTITY_MODEL_NAME),
      },
    });
    contextDispatch({ type: 'ADD_BANK_ACCOUNT', payload: { bankAccount } });
    invalidateOpenInvoices(queryClient, clientId);
    return bankAccount;
  };

  useSubscriptions<OnCreateBankAccountSubscription, OnUpdateBankAccountSubscription, OnDeleteBankAccountSubscription>(
    onCreateBankAccount,
    onUpdateBankAccount,
    onDeleteBankAccount,
    (data) => {
      contextDispatch({
        type: 'ADD_BANK_ACCOUNT',
        payload: { bankAccount: data.onCreateBankAccount as BankAccount },
      });
      invalidateOpenInvoices(queryClient, clientId);
      invalidatePayments(queryClient, clientId);
    },
    (data) => {
      const bankAccount = data.onUpdateBankAccount as BankAccount;
      const storedBankAccount = storedBankAccountsRef.current.find((candidate) => candidate.id === bankAccount.id);
      contextDispatch({ type: 'UPDATE_BANK_ACCOUNT', payload: { bankAccount } });
      invalidateOpenInvoices(queryClient, clientId);
      if (hasPaymentRelevantBankAccountChange(bankAccount, storedBankAccount)) {
        invalidatePayments(queryClient, clientId);
      }
    },
    (data) => {
      const { id } = data.onDeleteBankAccount as BankAccount;
      contextDispatch({
        type: 'DELETE_BANK_ACCOUNT',
        payload: { id },
      });
      invalidateOpenInvoices(queryClient, clientId);
      invalidatePayments(queryClient, clientId);
    }
  );

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (
        newData.name === CustomSubscriptionType.REFETCH &&
        getTypedCustomSubscriptionValueForRefetch(newData).table === BANK_ACCOUNT_TABLE_NAME
      ) {
        setFetchBankAccount();
      }
    },
    () => {},
    () => {}
  );

  const getBankAccountsForContact = (contactId: string) =>
    sortBankAccountsByCreation(allBankAccounts.filter((bankAccount) => bankAccount.contactId === contactId));

  const isBankAccountNumberTaken = (number: string, exceptBankAccountId?: string) => {
    const cleanNumber = getCleanBankAccountNumber(number);
    return allBankAccounts.some(
      (bankAccount) =>
        bankAccount.id !== exceptBankAccountId &&
        bankAccount.disabled !== true &&
        getCleanBankAccountNumber(bankAccount.number) === cleanNumber
    );
  };

  const getBankAccount = (id: string) => {
    if (loading) {
      return null;
    }
    return allBankAccounts.find((stateObject) => stateObject.id === id);
  };

  const updateBankAccount = async (original: BankAccount, updates: Partial<BankAccount>): Promise<BankAccount> => {
    const result = await mutationWithFetchBefore<BankAccount>(original.id, getQuery, updateMutation, {
      input: { ...cleanInputUpdate({ id: original.id, ...omit(updates, BACKEND_ONLY_BANK_ACCOUNT_FIELDS) }, false) },
    });
    contextDispatch({ type: 'UPDATE_BANK_ACCOUNT', payload: { bankAccount: result } });
    invalidateOpenInvoices(queryClient, clientId);
    return result;
  };

  const markBankAccountNotVerified = async (original: BankAccount): Promise<BankAccount> => {
    const result = await updateBankAccount(original, { ibanStatus: IbanStatus.KNOWN });
    if (!(result.isDefault ?? false)) {
      return result;
    }
    // The IBAN guard takes the default off an account that left Safe; shown now, not after its refetch.
    const notDefault = { ...result, isDefault: false };
    contextDispatch({ type: 'UPDATE_BANK_ACCOUNT', payload: { bankAccount: notDefault } });
    return notDefault;
  };

  const verifyBeneficiaryIban = async (bankAccount: BankAccount): Promise<BankAccount> => {
    const result = await mutation<BankAccount, VerifyBeneficiaryIbanMutationVariables>(verifyBeneficiaryIbanMutation, {
      bankAccountId: bankAccount.id,
      iban: bankAccount.number,
    });
    contextDispatch({ type: 'UPDATE_BANK_ACCOUNT', payload: { bankAccount: result } });
    invalidateOpenInvoices(queryClient, clientId);
    return result;
  };

  const applyServerUpdate = (original: BankAccount, result: BankAccount): BankAccount => {
    const bankAccount = { ...original, ...result };
    contextDispatch({ type: 'UPDATE_BANK_ACCOUNT', payload: { bankAccount } });
    invalidateOpenInvoices(queryClient, clientId);
    return bankAccount;
  };

  const setDefaultBankAccount = async (original: BankAccount): Promise<BankAccount> => {
    const result = await mutation<BankAccount, SetDefaultBankAccountMutationVariables>(setDefaultBankAccountMutation, {
      input: { bankAccountId: original.id },
    });
    allBankAccounts.forEach((bankAccount) => {
      const isPreviousDefault =
        bankAccount.contactId === original.contactId &&
        bankAccount.id !== original.id &&
        (bankAccount.isDefault ?? false);
      if (isPreviousDefault) {
        contextDispatch({
          type: 'UPDATE_BANK_ACCOUNT',
          payload: { bankAccount: { ...bankAccount, isDefault: false } },
        });
      }
    });
    return applyServerUpdate(original, result);
  };

  const deleteBankAccount = async (id: string): Promise<BankAccount> => {
    const bankAccount = getBankAccount(id)!;
    if (!financialTransactionsDelete) {
      return bankAccount;
    }

    await deleteEntityWithFetchBefore<Pick<BankAccount, 'id'>, DeleteMutationVariables>(
      { id },
      getQuery,
      deleteMutation
    );

    contextDispatch({ type: 'DELETE_BANK_ACCOUNT', payload: { id } });
    invalidateOpenInvoices(queryClient, clientId);
    return bankAccount;
  };

  const values = useMemo(
    () => ({
      bankAccounts: bankAccounts as BankAccount[],
      supplierBankAccounts,
      createBankAccount,
      getBankAccount,
      getBankAccountsForContact,
      isBankAccountNumberTaken,
      updateBankAccount,
      markBankAccountNotVerified,
      verifyBeneficiaryIban,
      setDefaultBankAccount,
      deleteBankAccount,
      setFetchBankAccount,
      error: null,
      loading,
      shouldFetchBankAccount: shouldFetch,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bankAccounts, supplierBankAccounts, allBankAccounts, loading, shouldFetch]
  );

  return <BankAccountContext.Provider value={values}>{children}</BankAccountContext.Provider>;
};

export const useBankAccounts = (): BankAccountContext => {
  const context = useContext<BankAccountContext | null>(BankAccountContext);

  if (isNil(context)) {
    throw new Error('`useBankAccounts` hook must be used within a `BankAccountContextProvider` component');
  }
  return context;
};
